import { Logic } from '~common/ui-logic/logic'
import { formatISODate } from '~common/utils/date-transformation/date-transformations'
import { ISO_TO_CURRENCY_SYMBOL } from '~common/utils/currency-normalization/currency-normalization'
import { AppContextType } from '~/app-context'
import { BankTransaction } from '~/storage/types'
import { SortFieldTransactions } from '../../logic'
import { DocumentTransactionAssociation } from '~/storage/types'
import { resolveCategoryLabel } from '~/features/transactions/services/categories'
import { getBookingCategoriesFromPlugin } from '~/features/plugins/utils'
import {
    PossibleColumns,
    PossibleColumnsFormat,
} from '~/features/ui-components/invoice-result/logic'

export interface TransactionResultDependencies {
    transactionDbId: BankTransaction['id'] | null
    onClick?: () => void
    onClickById?: (id: string) => void
    hideAvatar?: boolean
    isReferenced?: boolean // Add this line
    visibleColumns?: PossibleColumns[]
    specificMatchConfidence?: number | null // New prop
    context?: 'match-search'
    contextInvoiceId?: string
}

export interface TransactionResultState {
    showAllMatches: boolean
    showConfirmedMatches: boolean
    isNotePopupOpen: boolean
    noteEditValue: string
    isProcessingFileDrop: boolean
}

export class TransactionResultLogic extends Logic<
    TransactionResultDependencies & AppContextType,
    TransactionResultState
> {
    getInitialState = (): TransactionResultState => ({
        showAllMatches: false,
        showConfirmedMatches: false,
        isNotePopupOpen: false,
        noteEditValue: '',
        isProcessingFileDrop: false,
    })

    shouldReload(
        oldDeps: TransactionResultDependencies,
        newDeps: TransactionResultDependencies,
    ): boolean {
        return oldDeps.transactionDbId !== newDeps.transactionDbId
    }

    async processDroppedFileOnRow(file: File): Promise<void> {
        if (!this.deps.transactionDbId) return
        this.setState({ isProcessingFileDrop: true })
        this.deps.services.globalEvents.emit({
            type: 'showProcessingOverlay',
            label: 'Processing invoice...',
        })
        try {
            const result =
                await this.deps.services.invoiceProcessor.processInvoiceFromFileUpload(
                    file,
                )
            const newInvoiceId = result?.invoiceMetadata?.id
            if (!newInvoiceId) return
            await this.deps.services.transactionInvoiceMatching.confirmAssociation(
                newInvoiceId,
                this.deps.transactionDbId,
            )
        } finally {
            this.setState({ isProcessingFileDrop: false })
            this.deps.services.globalEvents.emit({
                type: 'hideProcessingOverlay',
            })
        }
    }

    initialize = async () => {
        // No data loading - handled in render
    }

    setShowAllMatches = () => {
        this.setState({ showAllMatches: !this.state.showAllMatches })
    }

    toggleShowConfirmedMatches = () => {
        this.setState({
            showConfirmedMatches: !this.state.showConfirmedMatches,
        })
    }

    formatTransactionData(
        deps: Pick<
            TransactionResultDependencies,
            'specificMatchConfidence' | 'contextInvoiceId' | 'context'
        >,
        transaction?: BankTransaction,
    ): Pick<
        PossibleColumnsFormat,
        | 'date'
        | 'amount'
        | 'currency'
        | 'category'
        | 'vendorName'
        | 'referenceText'
        | 'note'
        | 'createdAt'
        | 'matchConfidence'
    > | null {
        if (!transaction) return null

        const cache = this.deps.services.cache
        const settings = cache.getKey('settings')

        let matchConfidence: number | null = null
        if (deps.specificMatchConfidence !== undefined) {
            matchConfidence = deps.specificMatchConfidence
        } else if (transaction.id) {
            // Compute from cache
            const matchesByTransaction =
                cache.getKey('matchesByTransaction') || {}
            const assocIds = matchesByTransaction[transaction.id] || []
            const documentTransactionAssociations =
                cache.getKey('documentTransactionAssociations') || {}
            if (assocIds.length > 0) {
                // Take the highest confidence, prefer auto or user_confirmed
                const associations = assocIds
                    .map((id) => documentTransactionAssociations[id])
                    .filter((assoc): assoc is DocumentTransactionAssociation =>
                        Boolean(assoc),
                    )
                    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
                if (associations.length > 0) {
                    matchConfidence = associations[0].confidence
                }
            }

            // If referenceInvoiceId provided, compute specific confidence for that invoice
            if (deps.contextInvoiceId && transaction.id) {
                const matchesByInvoice = cache.getKey('matchesByInvoice') || {}
                const invAssocIds =
                    matchesByInvoice[deps.contextInvoiceId] || []
                const associations = invAssocIds
                    .map((id) => documentTransactionAssociations[id])
                    .filter(
                        (assoc): assoc is DocumentTransactionAssociation =>
                            Boolean(assoc) &&
                            assoc.transactionId === transaction.id,
                    )
                    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
                if (
                    associations.length > 0 &&
                    associations[0].method === 'auto'
                ) {
                    matchConfidence = associations[0].confidence
                }
            }
        }

        const isoDate = new Date(transaction.date).toISOString()
        const date = formatISODate(
            isoDate,
            settings?.dateFormat ?? 'MM/DD/YYYY',
        )

        return {
            date: date,
            amount: transaction.amount,
            currency:
                ISO_TO_CURRENCY_SYMBOL[transaction.currency ?? ''] ||
                transaction.currency,
            category: transaction.category
                ? resolveCategoryLabel(
                      transaction.category,
                      getBookingCategoriesFromPlugin(settings),
                  )
                : null,
            vendorName: transaction.vendorName || 'Unknown Vendor',
            referenceText: transaction.referenceText || '',
            note: transaction.note || '',
            createdAt: formatISODate(
                new Date(transaction.createdAt).toISOString(),
                settings?.dateFormat ?? 'MM/DD/YYYY',
            ),
            matchConfidence,
        }
    }

    resetMatch = async () => {
        if (this.deps.transactionDbId && this.deps.contextInvoiceId) {
            await this.deps.services.transactionInvoiceMatching.resetAssociationToAuto(
                this.deps.transactionDbId,
                this.deps.contextInvoiceId,
            )
        }
    }

    removeMatch = async () => {
        if (this.deps.transactionDbId && this.deps.contextInvoiceId) {
            await this.deps.services.transactionInvoiceMatching.removeAssociation(
                this.deps.transactionDbId,
                this.deps.contextInvoiceId,
            )
        }
    }

    openNotePopup = (currentNote: string) => {
        this.setState({
            isNotePopupOpen: true,
            noteEditValue: currentNote || '',
        })
    }

    closeNotePopup = async (currentNote: string) => {
        const { noteEditValue } = this.state
        const initialValue =
            this.deps.services.cache.getKey('transactions')?.[
                this.deps.transactionDbId!
            ]?.note
        this.setState({ isNotePopupOpen: false })

        if (currentNote !== initialValue) {
            if (
                this.deps.transactionDbId &&
                this.deps.services.bankTransaction
            ) {
                await this.deps.services.bankTransaction.update(
                    this.deps.transactionDbId,
                    { note: noteEditValue },
                )
            }
        }
    }

    setNoteEditValue = (value: string) => {
        this.setState({ noteEditValue: value.trim() })
    }
}
